import { h } from "preact";
import { useEffect, useState } from "preact/hooks";
import { useSelector } from "react-redux";

import uploadTask from "../../api/uploadTask.js";
import { errorWithReason, humanFileSize } from "../../helpers.js";
import Alert from "../Alert.jsx";
import Button from "../Button.jsx";
import ListItem from "../ListItem.jsx";
import UploadField from "../UploadField.jsx";
import { sendError } from "../../errors";
import { DEFAULT_FILE_SIZE, mapExtensions } from "../../uploadHelpers.js";
import i18n from "../../i18n.js";

export default function FileUpload(props) {
  const [uploadedFile, setUploadedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [currentImage, setCurrentImage] = useState(null);
  const maxFileSize = props.options.maxSize || DEFAULT_FILE_SIZE;

  const loggedOut = useSelector((state) => !state.currentUser.loggedIn);

  function loadTempFile(file) {
    var reader = new FileReader();
    reader.onload = function () {
      setCurrentImage(reader.result);
    };

    reader.readAsDataURL(file);
  }

  async function onChange(file) {
    setError(null);
    if (file.size > maxFileSize) {
      setError(
        `The file must not be bigger then ${humanFileSize(maxFileSize)}.`
      );
      return false;
    }

    loadTempFile(file);

    setUploading(true);
    try {
      const uploaded = await uploadTask.uploadFile(props.dataKey, file);
      if (uploaded) {
        props.onSelect(uploaded.fileId);
        setCurrentImage(uploaded.urls.avatar128);
      }
      setUploadedFile(uploaded);
    } catch (err) {
      sendError(err);
      if (err.message) {
        setError(err.message);
      } else {
        setError(
          errorWithReason(
            "The file could not uploaded. Please try again later and notify us if that happens again.",
            err
          )
        );
      }
    } finally {
      setUploading(false);
    }
  }

  async function handleDelete(event) {
    event.preventDefault();
    try {
      if (uploadedFile?.fileId) {
        await uploadTask.deleteFile(uploadedFile.fileId, props.dataKey);
        setUploadedFile(null);
      }
      setCurrentImage(null);
      setError(null);
      props.onSelect(null);
    } catch (err) {
      sendError(err);
      setError(
        errorWithReason(
          "The file could not deleted. Please try again later and notify us if that happens again.",
          err
        )
      );
    }
  }

  useEffect(async () => {
    if (props.answer) {
      try {
        const data = await uploadTask.getFileData(props.answer);
        setUploadedFile(data);
        setCurrentImage(data.urls.avatar128);
      } catch (err) {
        sendError(err);
      }
    }
  }, []);

  return (
    <div class="mt-32 mb-32">
      {error && <Alert error>{error}</Alert>}
      {currentImage ? (
        <div class="imageUpload__box">
          <img src={currentImage} class="imageUpload__image" />
          <p class="mt-0 mb-24">
            <Button iconFront="delete" secondary onClick={handleDelete}>
              {i18n.t("component.image_upload.delete_button")}
            </Button>
          </p>
          <p class="t-caption color-shades-grey">
            {i18n.t("component.upload_field.max_size", {
              size: humanFileSize(maxFileSize),
            })}
          </p>
        </div>
      ) : (
        <UploadField
          imageUpload
          dragDrop
          dragDropAllow={["image/jpeg", "image/png"]}
          id="question-suggested"
          name="answer"
          disabled={loggedOut || uploading}
          uploading={uploading}
          info={props.options.helperText}
          error={props.error}
          label={props.options.label}
          onChange={onChange}
          size={maxFileSize}
          accept={mapExtensions(props.options.allowedExtensions)}
        />
      )}
      {loggedOut && (
        <div class="mb-32">
          <ListItem
            class="mt-2"
            info
            iconFront="warning"
            secondLine={
              <span>
                You have to{" "}
                <a href={`/register?after=${location.pathname}`}>register</a> to
                upload files to Nioomi.
              </span>
            }
          >
            Attention
          </ListItem>
        </div>
      )}
    </div>
  );
}
